import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Award, ShieldCheck, Share2, Sparkles, Compass } from "lucide-react";

const PILLAR_ICONS = [Award, ShieldCheck, Share2];

const FALLBACK = {
    eyebrow: "Certificados",
    headline_pre: "Concluiu,",
    headline_em: "comprovou",
    intro: "",
    pillars: [],
    cta_eyebrow: "",
    cta_body: "",
};

export default function Certificados() {
    const { user } = useAuth();
    const [c, setC] = useState(FALLBACK);
    const [mine, setMine] = useState([]);

    useEffect(() => {
        api.get("/pages/certificados").then((r) => setC({ ...FALLBACK, ...(r.data?.content || {}) })).catch(() => {});
        if (user) api.get("/certificates").then((r) => setMine(r.data)).catch(() => {});
    }, [user]);

    return (
        <div className="min-h-screen relative grain">
            <Header />
            <main className="max-w-[1200px] mx-auto px-6 md:px-10 pt-14 pb-24">
                <div className="overline text-[#5C5C5C]">{c.eyebrow}</div>
                <h1 className="font-display font-light text-5xl md:text-6xl mt-2 tracking-tight">
                    {c.headline_pre} <span className="font-editorial italic text-holo">{c.headline_em}</span>
                </h1>
                <p className="font-body text-[#5C5C5C] mt-4 max-w-2xl">{c.intro}</p>

                <section data-testid="cert-pillars" className="mt-14 grid sm:grid-cols-3 gap-6">
                    {(c.pillars || []).map((p, i) => {
                        const Icon = PILLAR_ICONS[i] || Award;
                        return (
                            <article key={i} className="bg-white rounded-3xl p-8 border border-black/5">
                                <Icon className="w-6 h-6 text-[#1A1A1A]" />
                                <h3 className="font-display text-xl mt-4 tracking-tight">{p.title}</h3>
                                <p className="font-body text-sm text-[#5C5C5C] mt-3 leading-relaxed">{p.body}</p>
                            </article>
                        );
                    })}
                </section>

                {user && (
                    <section data-testid="cert-mine" className="mt-16">
                        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                            <div>
                                <div className="overline text-[#5C5C5C]">Seus certificados</div>
                                <h2 className="font-display text-3xl tracking-tight mt-1">Conquistas em sua jornada</h2>
                            </div>
                            <Link to="/perfil" className="btn-ghost text-sm">Ver no perfil</Link>
                        </div>
                        {mine.length > 0 ? (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {mine.map((cert) => (
                                    <Link key={cert.id} to={`/certificado/${cert.id}`} data-testid={`cert-card-${cert.id}`} className="group bg-white rounded-2xl p-6 border border-black/5 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all">
                                        <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                                        <div className="overline text-[#5C5C5C] mt-3">Certificado</div>
                                        <h3 className="font-display text-xl tracking-tight mt-1">{cert.journey_title}</h3>
                                        <div className="font-body text-xs text-[#5C5C5C] mt-3">{cert.hours}h · emitido em {new Date(cert.issued_at).toLocaleDateString("pt-BR")}</div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl p-10 border border-black/5 text-center">
                                <Compass className="w-7 h-7 text-[#5C5C5C] mx-auto" />
                                <p className="font-body text-[#5C5C5C] mt-3">Você ainda não concluiu nenhuma jornada. Comece pela <Link to="/jornadas" className="text-[#1A1A1A] underline">página de jornadas</Link>.</p>
                            </div>
                        )}
                    </section>
                )}

                {!user && (
                    <section className="mt-16 bg-white rounded-3xl p-10 md:p-14 border border-black/5 text-center">
                        <h2 className="font-display font-light text-3xl tracking-tight">{c.cta_eyebrow}</h2>
                        <p className="font-body text-[#5C5C5C] mt-3 max-w-xl mx-auto">{c.cta_body}</p>
                        <div className="flex flex-wrap gap-3 justify-center mt-7">
                            <Link to="/cadastro" className="btn-primary">Começar grátis</Link>
                            <Link to="/jornadas" className="btn-ghost">Explorar jornadas</Link>
                        </div>
                    </section>
                )}
            </main>
            <Footer />
        </div>
    );
}
